import React, { useCallback, useState } from 'react';
import {
  MapCameraChangedEvent,
  MapCameraProps,
} from '@vis.gl/react-google-maps';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';

type LogicProps = {
  listings: HospitalListingOutput[];
};

export const useLogic = ({ listings }: LogicProps) => {
  // ステーションが空の場合のマップの表示起点のデフォルト値
  const DEFAULT_LAT = 35.6659313;
  const DEFAULT_LNG = 139.750249;

  // listingsの平均位置とズームレベルを計算する関数
  const calculateMapBounds = useCallback(() => {
    if (!listings || listings.length === 0) {
      return {
        center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
        zoom: 15,
      };
    }

    // 有効な座標のみをフィルタリング
    const validListings = listings.filter(
      (listing) =>
        typeof listing.latitude === 'number' &&
        typeof listing.longitude === 'number',
    );

    if (validListings.length === 0) {
      return {
        center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
        zoom: 15,
      };
    }

    // 平均緯度・経度を計算
    const avgLat =
      validListings.reduce(
        (sum, listing) => sum + (listing.latitude as number),
        0,
      ) / validListings.length;
    const avgLng =
      validListings.reduce(
        (sum, listing) => sum + (listing.longitude as number),
        0,
      ) / validListings.length;

    // 最大・最小緯度経度を取得してバウンドを計算
    const latitudes = validListings.map(
      (listing) => listing.latitude as number,
    );
    const longitudes = validListings.map(
      (listing) => listing.longitude as number,
    );

    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);

    // バウンドの大きさに応じてズームレベルを計算
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 15; // デフォルト
    if (maxDiff > 0.5) zoom = 10;
    else if (maxDiff > 0.2) zoom = 11;
    else if (maxDiff > 0.1) zoom = 12;
    else if (maxDiff > 0.05) zoom = 13;
    else if (maxDiff > 0.02) zoom = 14;

    return {
      center: { lat: avgLat, lng: avgLng },
      zoom,
    };
  }, [listings]);

  const DefaultCamera = calculateMapBounds();

  const [cameraProps, setCameraProps] = useState<MapCameraProps>(DefaultCamera);
  const handleCameraChange = useCallback(
    (ev: MapCameraChangedEvent) => setCameraProps(ev.detail),
    [],
  );

  React.useEffect(() => {
    if (listings?.length > 0) {
      const newCamera = calculateMapBounds();
      setCameraProps(newCamera as MapCameraProps);
    }
  }, [listings, calculateMapBounds]);

  return {
    cameraProps,
    handleCameraChange,
  };
};
